import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  fetchContractors,
  createContractor,
  updateContractor,
  deleteContractor,
} from "../api";
import type { Contractor } from "@akfdev-com/shared/src/models/Contractor";

const emptyForm: Partial<Contractor> = {
  name: "",
  trade: "",
  contactDetails: "",
  insuranceExpiry: "",
  approved: false,
};

const ContractorRegisterPage: React.FC = () => {
  const [items, setItems] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Contractor> | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Contractor | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchContractors());
    } catch {
      setError("Failed to load contractors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setEditing({ ...emptyForm });
  };

  const startEdit = (c: Contractor) => {
    setEditingId(c.id);
    setEditing({ ...c });
  };

  const cancel = () => {
    setEditing(null);
    setEditingId(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name || !editing.trade || !editing.contactDetails) {
      setError("Name, trade and contact details are required.");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateContractor(editingId, editing);
      } else {
        await createContractor(editing);
      }
      cancel();
      await load();
    } catch {
      setError("Failed to save contractor.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteContractor(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch {
      setError("Failed to delete contractor.");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Contractor Register"
        description="Approved contractors with trade, contact details and insurance expiry."
        actions={
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            + New Contractor
          </button>
        }
      />

      {error && <ErrorAlert message={error} onRetry={load} />}
      {loading && <Spinner />}

      {editing && (
        <div className="bg-white shadow rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-gray-900">
            {editingId ? "Edit contractor" : "New contractor"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                value={editing.name || ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Trade</label>
              <input
                type="text"
                value={editing.trade || ""}
                onChange={(e) => setEditing({ ...editing, trade: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Contact details</label>
              <textarea
                rows={2}
                value={editing.contactDetails || ""}
                onChange={(e) => setEditing({ ...editing, contactDetails: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Insurance expiry</label>
              <input
                type="date"
                value={editing.insuranceExpiry || ""}
                onChange={(e) => setEditing({ ...editing, insuranceExpiry: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="flex items-center pt-6">
              <input
                id="approved"
                type="checkbox"
                checked={!!editing.approved}
                onChange={(e) => setEditing({ ...editing, approved: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <label htmlFor="approved" className="ml-2 text-sm text-gray-700">
                Approved
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={cancel}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {!loading && items.length === 0 && !editing && (
        <EmptyState
          title="No contractors yet"
          description="Add approved suppliers and trades so you can link them to inspections."
          actionText="+ New Contractor"
          onAction={startCreate}
        />
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Name</Th>
                <Th>Trade</Th>
                <Th>Contact</Th>
                <Th>Insurance Expiry</Th>
                <Th>Approved</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((c) => (
                <tr key={c.id}>
                  <Td>{c.name}</Td>
                  <Td>{c.trade}</Td>
                  <Td className="whitespace-pre-line">{c.contactDetails}</Td>
                  <Td>{c.insuranceExpiry || "—"}</Td>
                  <Td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        c.approved
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {c.approved ? "Yes" : "No"}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-3">
                      <button
                        onClick={() => startEdit(c)}
                        className="text-primary-700 hover:text-primary-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(c)}
                        className="text-red-700 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete contractor"
        message={`Delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={`px-4 py-2 text-sm text-gray-900 ${className || ""}`}>{children}</td>
);

export default ContractorRegisterPage;
